// src/ui/PianoKeyboard.js
// Computer-keyboard piano + recording input.
//
// Maps physical keyboard keys (e.code) to MIDI pitches via a per-layout
// offset table, plays the pitch through the currently selected track's
// instrument, and (when record mode is on AND the transport is playing)
// inserts a new note into the selected track at the current playhead
// position. Two layouts are supported:
//
//   - 'compact' (default): Ableton/FL-style 2-row, 18 keys, ~2.5 octaves.
//     The on-screen widget is a single row of 18 equal-sized buttons.
//   - 'full':              Same 18-key Ableton map as compact, plus 11
//     extra entries (X/C/V/B/N/M and digits 6-0) that fill the missing
//     whites and blacks of the low octave so the whole 2.5-octave range
//     is reachable from the keyboard. The on-screen widget renders as
//     a real piano (17 white keys + 12 black keys, standard piano
//     geometry, sorted by pitch ascending) — not 4 rows of virtual
//     keyboards.
//
// The active layout is toggled via a button in the piano banner. In
// both layouts, `[` and `]` (physical keys, e.code 'BracketLeft' /
// 'BracketRight') shift the octave window up/down.
//
// Also renders a small on-screen piano widget that mirrors the key map
// and highlights the currently-held keys. Clicking a key on the
// widget also triggers the same play/record path as a physical key.

import * as Tone from 'tone';
import { midiToName } from './NoteEditor.js';
import { recorder } from './recorder.js';
import { t, onLocaleChange } from '../i18n.js';

/**
 * Per-layout keymap. Each entry is `{ code, offset }` where `offset`
 * is the semitone from the A row's pitch (A_PITCH_FOR_OCTAVE). The
 * pitch for a given code is computed at runtime as
 *   A_PITCH_FOR_OCTAVE(this.octave) + offset
 * so the same key plays different pitches depending on the active
 * octave position.
 *
 * The Z slot is one octave below the A row, so it sits at offset -12.
 * The `;` slot is a fourth above the A row's B, so it sits at +16.
 * Both layouts therefore span offsets -12..+16 = 28 semitones
 * (2.33 octaves). The full layout just adds more letter/digit entries
 * to fill the missing whites and blacks of the low octave.
 */
const LAYOUTS = {
  // 2-row "compact" layout:
  //   Top row (sharps):  W  E    T  Y  U    O  P
  //   Bottom (whites):   A  S  D  F  G  H  J  K  L  ;
  //                       C# D#  F# G# A#  C# D#
  //                       C  D  E  F  G  A  B  C  D  E
  //   Plus KeyZ          C  (one octave below the A row)
  compact: {
    keys: [
      { code: 'KeyZ',       offset: -12 },
      { code: 'KeyA',       offset:   0 },
      { code: 'KeyW',       offset:   1 },
      { code: 'KeyS',       offset:   2 },
      { code: 'KeyE',       offset:   3 },
      { code: 'KeyD',       offset:   4 },
      { code: 'KeyF',       offset:   5 },
      { code: 'KeyT',       offset:   6 },
      { code: 'KeyG',       offset:   7 },
      { code: 'KeyY',       offset:   8 },
      { code: 'KeyH',       offset:   9 },
      { code: 'KeyU',       offset:  10 },
      { code: 'KeyJ',       offset:  11 },
      { code: 'KeyK',       offset:  12 },
      { code: 'KeyO',       offset:  13 },
      { code: 'KeyL',       offset:  14 },
      { code: 'KeyP',       offset:  15 },
      { code: 'Semicolon',  offset:  16 }
    ]
  },
  // "Full" layout: same Ableton/FL 2-row key map as compact (~2.5
  // octaves, 18 keys), plus 6 extra entries on the bottom row
  // (X, C, V, B, N, M) that fill the missing white keys of the
  // low octave in the widget. The 18-key compact map only covers
  // Z (=C3) in the low octave, so D3..B3 were mouse-click-only.
  // Adding the 6 bottom-row letters as the remaining whites turns
  // the bottom row into a full C-major scale of the low octave:
  //   Z=C3, X=D3, C=E3, V=F3, B=G3, N=A3, M=B3
  // The 5 black keys in the low octave (C#3 D#3 F#3 G#3 A#3) are
  // still mouse-only since we don't have a way to reach them with
  // the bottom row alone. The on-screen widget visualizes the full
  // 2.5-octave range (Z..;) as 17 white + 12 black piano keys.
  full: {
    keys: [
      // Main Ableton map (same as compact):
      { code: 'KeyZ',       offset: -12 },
      { code: 'KeyA',       offset:   0 },
      { code: 'KeyW',       offset:   1 },
      { code: 'KeyS',       offset:   2 },
      { code: 'KeyE',       offset:   3 },
      { code: 'KeyD',       offset:   4 },
      { code: 'KeyF',       offset:   5 },
      { code: 'KeyT',       offset:   6 },
      { code: 'KeyG',       offset:   7 },
      { code: 'KeyY',       offset:   8 },
      { code: 'KeyH',       offset:   9 },
      { code: 'KeyU',       offset:  10 },
      { code: 'KeyJ',       offset:  11 },
      { code: 'KeyK',       offset:  12 },
      { code: 'KeyO',       offset:  13 },
      { code: 'KeyL',       offset:  14 },
      { code: 'KeyP',       offset:  15 },
      { code: 'Semicolon',  offset:  16 },
      // Bottom-row extension: fill the missing whites of the low
      // octave (D3..B3) so the bottom row plays the full C major
      // scale of the low octave. Z is the existing C3 entry.
      { code: 'KeyX',       offset: -10 },  // D3
      { code: 'KeyC',       offset:  -8 },  // E3
      { code: 'KeyV',       offset:  -7 },  // F3
      { code: 'KeyB',       offset:  -5 },  // G3
      { code: 'KeyN',       offset:  -3 },  // A3
      { code: 'KeyM',       offset:  -1 },  // B3
      // Digit-row extension: fill the missing blacks of the low
      // octave (C#3 D#3 F#3 G#3 A#3) so the digit row plays the
      // sharps of the low octave. Together with the bottom-row
      // letters above, the entire C3..B3 chromatic range is now
      // reachable from the keyboard.
      { code: 'Digit6',     offset: -11 },  // C#3
      { code: 'Digit7',     offset:  -9 },  // D#3
      { code: 'Digit8',     offset:  -6 },  // F#3
      { code: 'Digit9',     offset:  -4 },  // G#3
      { code: 'Digit0',     offset:  -2 }   // A#3
    ]
  }
};

/**
 * Map of physical-key code -> the letter shown on the on-screen
 * widget's shortcut label. Static — doesn't change with octave or
 * layout. (Same physical key shows the same letter in both modes.)
 */
const KEY_LETTER = new Map([
  ['KeyZ', 'Z'],
  ['KeyA', 'A'], ['KeyW', 'W'], ['KeyS', 'S'], ['KeyE', 'E'],
  ['KeyD', 'D'], ['KeyF', 'F'], ['KeyT', 'T'], ['KeyG', 'G'],
  ['KeyY', 'Y'], ['KeyH', 'H'], ['KeyU', 'U'], ['KeyJ', 'J'],
  ['KeyK', 'K'], ['KeyO', 'O'], ['KeyL', 'L'], ['KeyP', 'P'],
  ['Semicolon', ';'],
  ['KeyQ', 'Q'],
  ['KeyX', 'X'], ['KeyC', 'C'], ['KeyV', 'V'], ['KeyB', 'B'], ['KeyN', 'N'], ['KeyM', 'M'],
  ['Digit1', '1'], ['Digit2', '2'], ['Digit3', '3'],
  ['Digit4', '4'], ['Digit5', '5'], ['Digit6', '6'],
  ['Digit7', '7'], ['Digit8', '8'], ['Digit9', '9'],
  ['Digit0', '0']
]);

/**
 * Octave window range. The "A row" pitch is C1..C6 across the six
 * positions; the Z slot is always the C an octave below, and `;` is
 * always the E an octave-plus-a-fourth above. Default position 3
 * matches the original (pre-octave-switching) layout: Z=C3, A=C4.
 */
const OCTAVE_MIN = 0;
const OCTAVE_MAX = 5;
const OCTAVE_DEFAULT = 3;
const A_PITCH_FOR_OCTAVE = (oct) => 24 + oct * 12;

const NOTE_DURATION = 0.5;   // seconds — fixed for recorded notes
const NOTE_VELOCITY = 0.8;

export class PianoKeyboard {
  /**
   * @param {HTMLElement} rootEl
   * @param {{
   *   trackManager: import('../tracks/TrackManager.js').TrackManager,
   *   sequencer: import('../sequencer/Sequencer.js').Sequencer,
   * }} options
   */
  constructor(rootEl, { trackManager, sequencer }) {
    this.root = rootEl;
    this.root.classList.add('piano-keyboard-root');
    this.tm = trackManager;
    this.sequencer = sequencer;
    this.activeKeys = new Set(); // pitches currently held
    // Current octave window position. The "A row" pitch is
    // A_PITCH_FOR_OCTAVE(this.octave); Z plays A_row-12, `;` plays
    // A_row+16. Default position 3 = A row C4 (matches the original
    // layout before octave switching was added).
    this.octave = OCTAVE_DEFAULT;
    // Active layout ('compact' or 'full'). Toggled via the button in
    // the piano banner. Default: 'compact' (the original 2-row look).
    this.layout = 'compact';
    // Mobile-friendly sustain: tap a key to start ringing, tap again to
    // release. Does NOT affect the desktop Shift+key path — that remains
    // always-sustain. Toggled by the SUS button in the piano banner.
    this.sustainMode = false;
    // Per-instance code -> offset map, rebuilt from LAYOUTS[this.layout]
    // whenever the layout changes. Stored on the instance rather than
    // module-level because the table depends on the active layout.
    this.codeToOffset = new Map();
    this._rebuildCodeMap();
    // Sustained (Shift+key) notes: pitch -> { noteId?, startTime }
    //   - noteId:    when recording, the id of the placeholder note we
    //                inserted at attack time. Updated with the real
    //                duration on keyup. Null for non-recording sessions.
    //   - startTime: AudioContext time when the attack fired. Used both
    //                to size the recorded note and to guard against
    //                double-release.
    this.sustainedNotes = new Map();
    // True once the user has pressed Play and the Transport is
    // actively running or paused. We track this via Tone.Transport
    // events (rather than just reading state === 'started') because
    // Transport.start(time, offset) is scheduled — state only flips
    // to 'started' 0.1s after play() is called, so a key press in
    // that window would otherwise miss the recording condition.
    this.isPlaybackActive = false;

    this.render();
    this._unsubSel = this.sequencer.onSelectionChange(() => {
      this._resubscribeSelectedTrack();
      this.render();
    });
    this._unsubRec = recorder.onChange(() => this.render());
    // Subscribe to the currently-selected track so its banner metadata
    // (name, color) stays in sync when its instrument is swapped. We
    // deliberately do NOT subscribe to this.tm.onChange — that fires
    // for every note addition across every track, which would trigger a
    // full piano-widget re-render on every recorded keypress. On iOS
    // Safari, wiping the piano DOM during a touchstart or keydown
    // handler can break the corresponding touchend/keyup event, leaving
    // pressed keys stuck red until the user stops recording.
    //
    // Updates are applied in-place via _updateBanner() so the keyboard
    // DOM (the part iOS cares about during a touch) is preserved.
    this._resubscribeSelectedTrack();
    // Re-render on locale change to translate button labels and hints.
    onLocaleChange(() => this.render());

    // Subscribe to Transport state changes. The events fire on every
    // start/pause/stop, including the scheduled 0.1s-later start. This
    // gives us a robust playback-active flag that's correct even when
    // the user presses a key in the gap between play() and the actual
    // start time.
    const onStart = () => { this.isPlaybackActive = true; };
    const onPause = () => { this.isPlaybackActive = false; };
    const onStop  = () => {
      this.isPlaybackActive = false;
      // Release any sustained (Shift+key) notes that were still ringing
      // when the transport stopped — otherwise the sampler voices would
      // keep ringing through their release envelope and the placeholder
      // notes would never get a final duration.
      this._releaseAllSustained();
    };
    Tone.Transport.on('start', onStart);
    Tone.Transport.on('pause', onPause);
    Tone.Transport.on('stop', onStop);

    // Bind handlers once and attach them to the root element (which
    // survives every render() because we only clear innerHTML, not the
    // root itself). This way the click handler isn't lost when re-renders
    // replace the per-button DOM nodes mid-click.
    this._onRootMouseMove = this._onRootMouseMove.bind(this);
    this._onRootMouseLeave = this._onRootMouseLeave.bind(this);
    this._onRootTouchStart = this._onRootTouchStart.bind(this);
    this._onRootTouchMove = this._onRootTouchMove.bind(this);
    this._onRootTouchEnd = this._onRootTouchEnd.bind(this);
    this._onRootTouchCancel = this._onRootTouchCancel.bind(this);
    // _hoverPitch: the pitch currently under the cursor/finger, or
    // null when nothing is hovered. Updated by _hoverToPoint. Used to
    // detect pitch changes mid-gesture so we only re-trigger when the
    // cursor moves to a different key.
    this._hoverPitch = null;
    // _touchOnKey: true once a touchstart lands on a piano key, so
    // subsequent touchmove/touchend know whether to engage hover
    // tracking. False for touches on banner buttons or empty space.
    this._touchOnKey = false;
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onLayoutToggle = () => {
      this.setLayout(this.layout === 'compact' ? 'full' : 'compact');
    };
    this._onOctaveDown    = () => this.setOctave(this.octave - 1);
    this._onOctaveUp      = () => this.setOctave(this.octave + 1);
    this._onSustainToggle = () => this.setSustainMode(!this.sustainMode);
    this._onWindowBlur = () => this._releaseAllSustained();
    // Hover-based mouse input: moving the cursor over a key plays it,
    // moving off releases it. No mousedown required — the whole
    // keyboard acts like a hover-to-play surface. The keyboard is the
    // mousemove boundary, so listening on `this.root` (which is larger
    // than the keyboard panel and includes the banner) is fine —
    // elementFromPoint only resolves to .piano-key descendants.
    this.root.addEventListener('mousemove', this._onRootMouseMove);
    this.root.addEventListener('mouseleave', this._onRootMouseLeave);
    // Touch: a finger is a sustained "hover". touchstart begins the
    // gesture (plays the touched key), touchmove updates the held
    // pitch as the finger drags, touchend/touchcancel releases.
    this.root.addEventListener('touchstart', this._onRootTouchStart, { passive: false });
    this.root.addEventListener('touchmove', this._onRootTouchMove, { passive: false });
    this.root.addEventListener('touchend', this._onRootTouchEnd, { passive: false });
    this.root.addEventListener('touchcancel', this._onRootTouchCancel, { passive: false });
    // Window-level listeners for physical keyboard input.
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    // If the window loses focus while a key is held, keyup never fires
    // for the held key — release sustained notes so they don't ring on
    // after the user alt-tabs away.
    window.addEventListener('blur', this._onWindowBlur);
  }

  /**
   * Root-level mousemove: find the .piano-key under the cursor and
   * play it (or release it if the cursor is off the keyboard). No
   * mousedown is required — the keyboard acts as a hover-to-play
   * surface. Dragging the cursor across keys plays them in sequence
   * (this is what the user calls "swipe on the keyboard").
   */
  _onRootMouseMove(e) {
    this._hoverToPoint(e.clientX, e.clientY);
  }

  /**
   * Root-level mouseleave: the cursor has left the piano panel
   * entirely. Release any hovered key.
   */
  _onRootMouseLeave() {
    if (this._hoverPitch !== null) {
      this._setActive(this._hoverPitch, false);
      this._hoverPitch = null;
    }
  }

  /**
   * Root-level touchstart: a finger is a sustained hover. Play the
   * touched key (if any), then track the finger via touchmove for
   * swipe. We only preventDefault when the touch started on a piano
   * key — touches on banner buttons (octave up/down, sustain, layout
   * toggle) need their click handlers to fire, and touches on empty
   * panel space need to scroll the page. The iOS double-tap-to-zoom
   * delay only matters on the keyboard itself.
   *
   * _touchOnKey remembers whether the current gesture started on a
   * piano key, so touchmove/touchend can short-circuit non-key
   * gestures.
   */
  _onRootTouchStart(e) {
    if (e.changedTouches.length === 0) return;
    const t = e.changedTouches[0];
    // elementFromPoint correctly resolves absolutely-positioned black
    // keys in the Full layout that overlay the white-key row. closest()
    // from e.target can mis-resolve for them.
    const target = document.elementFromPoint(t.clientX, t.clientY);
    const btn = target ? target.closest('.piano-key') : null;
    this._touchOnKey = !!(btn && this.root.contains(btn));
    if (!this._touchOnKey) return;
    e.preventDefault();
    this._hoverToPoint(t.clientX, t.clientY);
  }

  /**
   * Root-level touchmove: update the hovered pitch as the finger
   * drags. Single-finger model — only the first touch drives the
   * hover state. Only engages when the gesture started on a piano
   * key; otherwise the touch is on a button or empty space and the
   * page should scroll normally.
   */
  _onRootTouchMove(e) {
    if (!this._touchOnKey) return;
    if (e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    this._hoverToPoint(t.clientX, t.clientY);
  }

  /**
   * Root-level touchend: the finger lifted. Release the hovered key
   * (if any) and clear the on-key flag.
   */
  _onRootTouchEnd() {
    this._touchOnKey = false;
    if (this._hoverPitch !== null) {
      this._setActive(this._hoverPitch, false);
      this._hoverPitch = null;
    }
  }

  /**
   * Root-level touchcancel: same as touchend — the touch was
   * interrupted (e.g. system alert). Release the hovered key.
   */
  _onRootTouchCancel() {
    this._onRootTouchEnd();
  }

  /**
   * Shared hover logic for mouse and touch. Given a screen point,
   * attack the key under it (and release any previously-hovered
   * key that the cursor/finger is no longer over). Off-keyboard
   * points release the held key. The same point that produced the
   * hover is a no-op (avoids re-triggering on jittery movement).
   *
   * In sustain mode, hover is a no-op: the spec is "tap to toggle"
   * and a hover model would create stuck notes whenever the cursor
   * leaves the keyboard.
   */
  _hoverToPoint(x, y) {
    if (this.sustainMode) return;
    const target = document.elementFromPoint(x, y);
    const btn = target ? target.closest('.piano-key') : null;
    if (!btn || !this.root.contains(btn)) {
      // Off the keyboard — release the current pitch so the note stops.
      if (this._hoverPitch !== null) {
        this._setActive(this._hoverPitch, false);
        this._hoverPitch = null;
      }
      return;
    }
    const pitch = parseInt(btn.dataset.pitch, 10);
    if (Number.isNaN(pitch)) return;
    if (pitch === this._hoverPitch) return; // no change — same key
    // Switch to the new pitch: release the old, attack the new.
    if (this._hoverPitch !== null) this._setActive(this._hoverPitch, false);
    this._playAndMaybeRecord(pitch);
    this._setActive(pitch, true);
    this._hoverPitch = pitch;
  }

  /**
   * The on-screen widget. Two stacked rows of keys: a row of black
   * keys (sharps) on top, a row of white keys on the bottom, each
   * labeled with the shortcut letter and the note name.
   */
  render() {
    this.root.innerHTML = '';

    // Track-name banner at the top of the panel.
    const track = this.selectedTrack();
    const banner = document.createElement('div');
    banner.className = 'piano-banner';
    if (track) {
      const swatch = document.createElement('span');
      swatch.className = 'piano-swatch';
      swatch.style.setProperty('--swatch-color', track.color);
      const name = document.createElement('span');
      name.className = 'piano-target-name';
      name.textContent = track.name;
      const rec = document.createElement('span');
      rec.className = 'piano-rec-indicator' + (recorder.recording ? ' active' : '');
      rec.textContent = recorder.recording ? '● REC' : '';
      // Octave indicator — shows the lowest and highest pitches the
      // current window covers. Both layouts span the same 2.5-octave
      // physical-key range (offsets -12..+16 from the A row, Z=low C
      // and ;=high E). The compact layout uses button columns instead
      // of piano geometry, but the pitch window is identical.
      // Inserted BEFORE the layout toggle and shift hint so the hint's
      // margin-left:auto still pushes it to the far right of the banner.
      const octaveInd = document.createElement('span');
      octaveInd.className = 'piano-octave-indicator piano-label';
      const aPitch = A_PITCH_FOR_OCTAVE(this.octave);
      const rangeLow = aPitch - 12;
      const rangeHigh = aPitch + 16;
      octaveInd.textContent = `${midiToName(rangeLow)}–${midiToName(rangeHigh)}`;
      // Layout toggle button — reuses the .piano-shift-hint pill style.
      // Layout switch toggle — a fixed-width two-state switch with a
      // sliding thumb. Both labels (Compact / Full) are always rendered
      // so the surrounding banner items don't shift when the state
      // changes. role="switch" + aria-checked give correct a11y semantics.
      const layoutBtn = document.createElement('button');
      layoutBtn.type = 'button';
      layoutBtn.className = 'piano-layout-toggle piano-shift-hint' + (this.layout === 'full' ? ' is-active' : '');
      layoutBtn.setAttribute('role', 'switch');
      layoutBtn.setAttribute('aria-checked', this.layout === 'full' ? 'true' : 'false');

      const compactLabel = document.createElement('span');
      compactLabel.className = 'piano-layout-toggle-option';
      compactLabel.textContent = t('piano.compact');
      const fullLabel = document.createElement('span');
      fullLabel.className = 'piano-layout-toggle-option';
      fullLabel.textContent = t('piano.full');

      layoutBtn.appendChild(compactLabel);
      layoutBtn.appendChild(fullLabel);
      layoutBtn.title = this.layout === 'compact' ? t('piano.titleFull') : t('piano.titleCompact');
      layoutBtn.addEventListener('click', this._onLayoutToggle);

      // Octave − / Octave + buttons — mobile-friendly octave shift.
      // Disabled at the boundaries for visual + a11y feedback.
      const octDownBtn = document.createElement('button');
      octDownBtn.type = 'button';
      octDownBtn.className = 'piano-octave-down piano-shift-hint';
      octDownBtn.textContent = t('piano.octDown');
      octDownBtn.title = t('piano.titleOctDown');
      octDownBtn.disabled = this.octave === OCTAVE_MIN;
      octDownBtn.addEventListener('click', this._onOctaveDown);

      const octUpBtn = document.createElement('button');
      octUpBtn.type = 'button';
      octUpBtn.className = 'piano-octave-up piano-shift-hint';
      octUpBtn.textContent = t('piano.octUp');
      octUpBtn.title = t('piano.titleOctUp');
      octUpBtn.disabled = this.octave === OCTAVE_MAX;
      octUpBtn.addEventListener('click', this._onOctaveUp);

      // Sustain toggle — when ON, tap a key to start ringing, tap again
      // to release. Independent of the desktop Shift+key path.
      const susBtn = document.createElement('button');
      susBtn.type = 'button';
      susBtn.className = 'piano-sustain-toggle piano-shift-hint' + (this.sustainMode ? ' is-active' : '');
      susBtn.textContent = t('piano.sustain');
      susBtn.title = t('piano.titleSustain');
      susBtn.setAttribute('aria-pressed', this.sustainMode ? 'true' : 'false');
      susBtn.addEventListener('click', this._onSustainToggle);

      const shiftHint = document.createElement('span');
      shiftHint.className = 'piano-label';
      shiftHint.textContent = t('piano.bHint');
      banner.appendChild(swatch);
      banner.appendChild(name);
      banner.appendChild(rec);
      banner.appendChild(octDownBtn);
      banner.appendChild(octUpBtn);
      banner.appendChild(octaveInd);
      banner.appendChild(susBtn);
      banner.appendChild(shiftHint);
      banner.appendChild(layoutBtn);
    } else {
      const hint = document.createElement('span');
      hint.className = 'piano-hint';
      hint.textContent = t('piano.noTrackSelected');
      banner.appendChild(hint);
    }
    this.root.appendChild(banner);

    // Keys.
    const keyboard = document.createElement('div');
    keyboard.className = 'piano-keys';
    keyboard.dataset.layout = this.layout;
    const aPitch = A_PITCH_FOR_OCTAVE(this.octave);
    const offsets = LAYOUTS[this.layout].keys;
    if (this.layout === 'full') {
      // Piano visualization of the main 2.5-octave Ableton range
      // (Z..; — 29 pitches = 17 whites + 12 blacks). The bottom-row
      // extension entries (X, C, V, B, N, M) now fill the missing
      // whites of the low octave (D3..B3), so the widget shows
      // those pitches as labeled whites.
      const lowOffset = -12; // Z's offset
      const highOffset = 16; // ;'s offset
      const whiteCount = 17; // 17 whites across 2.5 octaves
      const offsetToCode = new Map(offsets.map((k) => [k.offset, k.code]));
      let whiteIndex = 0; // count of white keys seen so far
      for (let i = lowOffset; i <= highOffset; i++) {
        const pitch = aPitch + i;
        const isBlack = [1, 3, 6, 8, 10].includes(pitch % 12);
        const code = offsetToCode.get(i); // undefined for unlabeled pitches
        const key = this._buildKey(pitch, code);
        if (isBlack) {
          // Center this black key on the seam between the most recent
          // white and the next one. CSS translateX(-50%) centers it.
          key.classList.add('piano-black-positioned');
          key.style.left = `${(whiteIndex / whiteCount) * 100}%`;
        } else {
          whiteIndex += 1;
        }
        keyboard.appendChild(key);
      }
    } else {
      // Compact: single row of 18 keys (existing 2-row visual layout).
      for (const { code, offset } of offsets) {
        const pitch = aPitch + offset;
        keyboard.appendChild(this._buildKey(pitch, code));
      }
    }
    this.root.appendChild(keyboard);
  }

  _buildKey(pitch, code) {
    const isBlack = [1, 3, 6, 8, 10].includes(pitch % 12);
    const btn = document.createElement('button');
    // type="button" so clicks never trigger any form-submit default.
    btn.type = 'button';
    btn.className = 'piano-key' + (isBlack ? ' black' : ' white');
    btn.dataset.pitch = String(pitch);
    btn.title = midiToName(pitch);
    if (this.activeKeys.has(pitch)) btn.classList.add('active');
    const label = document.createElement('span');
    label.className = 'piano-key-shortcut';
    label.textContent = KEY_LETTER.get(code) || '';
    const name = document.createElement('span');
    name.className = 'piano-key-name';
    name.textContent = midiToName(pitch);
    btn.appendChild(label);
    btn.appendChild(name);
    // No per-button listeners — the constructor attaches a single
    // delegated mousedown/mouseup pair on this.root that survives every
    // render() (this.root is never replaced, only its innerHTML is).
    return btn;
  }

  _onKeyDown(e) {
    if (!this._shouldHandle(e)) return;
    // Octave-shift keys are handled before the pitch lookup — they
    // don't produce a sound, just move the active window.
    if (e.code === 'BracketLeft') {
      e.preventDefault();
      this.setOctave(this.octave - 1);
      return;
    }
    if (e.code === 'BracketRight') {
      e.preventDefault();
      this.setOctave(this.octave + 1);
      return;
    }
    const pitch = this._pitchForCode(e.code);
    if (pitch === undefined) return;
    if (e.repeat) return; // OS key-repeat — don't fire multiple times.
    e.preventDefault();
    if (e.shiftKey) this._sustainAttack(pitch);
    else this._playAndMaybeRecord(pitch);
    this._setActive(pitch, true);
  }

  _onKeyUp(e) {
    // Bracket keys have no note to release.
    if (e.code === 'BracketLeft' || e.code === 'BracketRight') return;
    const pitch = this._pitchForCode(e.code);
    if (pitch === undefined) return;
    this._setActive(pitch, false);
    // If this pitch is currently sustained (Shift+key), release it.
    if (this.sustainedNotes.has(pitch)) this._sustainRelease(pitch);
  }

  /**
   * Rebuild this.codeToOffset from the active layout's offset table.
   * Called from the constructor and from setLayout().
   */
  _rebuildCodeMap() {
    this.codeToOffset = new Map(
      LAYOUTS[this.layout].keys.map((k) => [k.code, k.offset])
    );
  }

  /**
   * Look up the MIDI pitch for a physical-key code at the current
   * octave and layout. Returns undefined for codes that aren't in
   * the active key map (so the caller can early-return).
   */
  _pitchForCode(code) {
    const offset = this.codeToOffset.get(code);
    if (offset === undefined) return undefined;
    return A_PITCH_FOR_OCTAVE(this.octave) + offset;
  }

  /**
   * Move the active octave window by `delta` and re-render. Clamped
   * to [OCTAVE_MIN, OCTAVE_MAX]. No-op (no re-render) if the value
   * doesn't change — so holding `]` at the top of the range doesn't
   * thrash the DOM.
   *
   * Any currently-held note keeps ringing at its original pitch even
   * after the window moves: `sustainedNotes` and `activeKeys` are
   * keyed by MIDI pitch, not by code, so they survive the shift.
   */
  setOctave(n) {
    const clamped = Math.max(OCTAVE_MIN, Math.min(OCTAVE_MAX, n | 0));
    if (clamped === this.octave) return;
    this.octave = clamped;
    this.render();
  }

  /**
   * Switch between the 'compact' (2-row, 18 keys) and 'full'
   * (4-row, 24 keys) layouts. Re-renders the piano widget and
   * rebuilds the keymap lookup. The active octave is preserved.
   *
   * Any currently-held or sustained notes are released first: their
   * pitches may not be reachable in the new layout, so leaving them
   * ringing would be confusing. The user re-presses in the new mode.
   */
  setLayout(name) {
    if (name !== 'compact' && name !== 'full') return;
    if (name === this.layout) return;
    this.layout = name;
    this._releaseAllSustained();
    this.activeKeys.clear();
    this._rebuildCodeMap();
    this.render();
  }

  /**
   * Enable/disable the tap-to-sustain mode for pointer/touch input.
   * Turning it OFF releases all currently-sustained notes — leaving
   * them ringing would be confusing (the next tap would just toggle
   * the first one off, not re-attack).
   *
   * Note: this is independent of the desktop Shift+key path. On a
   * desktop with a physical keyboard, Shift+key still always sustains,
   * regardless of this toggle.
   */
  setSustainMode(on) {
    this.sustainMode = !!on;
    if (!on) this._releaseAllSustained();
    this.render();
  }

  /**
   * Start a sustained note. Rings until _sustainRelease(pitch) is called
   * (typically on keyup). If recording is on, also inserts a placeholder
   * note at the current playhead whose duration is finalized in
   * _sustainRelease.
   */
  _sustainAttack(pitch) {
    if (this.sustainedNotes.has(pitch)) return; // already ringing
    const track = this.selectedTrack();
    if (!track) return;
    const inst = track.instrument;
    if (!inst) return;
    const now = Tone.now();
    try {
      inst.triggerAttack(pitch, now, NOTE_VELOCITY);
    } catch { /* instrument not ready */ }

    // If recording, insert a placeholder note with a tiny default
    // duration; we'll resize it in _sustainRelease when we know the
    // real held length.
    let noteId = null;
    if (recorder.recording && this.isPlaybackActive) {
      try {
        const newNote = track.addNote({
          time: Tone.Transport.seconds || 0,
          duration: 0.05, // tiny placeholder; resized on release
          pitch,
          velocity: NOTE_VELOCITY
        });
        if (newNote) {
          noteId = newNote.id;
          track.markRecent(newNote.id);
        }
      } catch { /* noop */ }
    }

    // Capture the track + instrument references at attack time so the
    // matching release goes to the right instrument even if the user
    // switches the selected track mid-hold.
    this.sustainedNotes.set(pitch, { track, inst, noteId, startTime: now });
  }

  /**
   * Release a sustained note started by _sustainAttack. If we inserted
   * a recording placeholder, resize it to the actual held duration so
   * the on-screen note matches what the user heard.
   */
  _sustainRelease(pitch) {
    const entry = this.sustainedNotes.get(pitch);
    if (!entry) return;
    this.sustainedNotes.delete(pitch);
    const now = Tone.now();
    try {
      entry.inst.triggerRelease(pitch, now);
    } catch { /* noop */ }
    if (entry.noteId) {
      const heldSec = Math.max(0.05, now - entry.startTime);
      try { entry.track.updateNote(entry.noteId, { duration: heldSec }); } catch { /* noop */ }
    }
  }

  /**
   * Release every currently-sustained note. Used on transport stop and
   * on window blur, when we can't rely on a per-pitch keyup to fire.
   */
  _releaseAllSustained() {
    if (this.sustainedNotes.size === 0) return;
    for (const pitch of Array.from(this.sustainedNotes.keys())) {
      this._sustainRelease(pitch);
    }
  }

  /**
   * Shared play + (optional) record path. Called from both physical
   * keypresses and on-screen key clicks.
   */
  _playAndMaybeRecord(pitch) {
    const track = this.selectedTrack();
    if (!track) return;
    const inst = track.instrument;
    if (!inst) return;

    // Always play a sound so the user can audition pitches.
    try {
      inst.triggerAttackRelease(pitch, NOTE_DURATION, Tone.now(), NOTE_VELOCITY);
    } catch { /* instrument not ready */ }

    // If recording into a clip, route to the pending clip instead of the track
    if (recorder.pendingClip) {
      const clipTime = (Tone.Transport.seconds || 0) - recorder.recStartTime;
      recorder.pendingClip.notes.push({
        time: clipTime,
        duration: NOTE_DURATION,
        pitch,
        velocity: NOTE_VELOCITY
      });
      return; // don't add to track
    }

    // Record only when record mode is on AND the transport has been
    // started (or paused mid-playback). We use the isPlaybackActive
    // flag (updated via Tone.Transport 'start'/'pause'/'stop' events)
    // rather than Tone.Transport.state === 'started' directly,
    // because Transport.start(time, offset) is scheduled ~0.1s in the
    // future — without the event subscription, a key pressed in the
    // gap between play() and the actual start time would not be
    // recorded.
    if (recorder.recording && this.isPlaybackActive) {
      try {
        const newNote = track.addNote({
          time: Tone.Transport.seconds || 0,
          duration: NOTE_DURATION,
          pitch,
          velocity: NOTE_VELOCITY
        });
        // Tag the new note so the Sequencer's draw loop can flash it
        // briefly, giving the user instant visual confirmation that
        // the keypress was captured.
        if (newNote) track.markRecent(newNote.id);
      } catch { /* noop */ }
    }
  }

  /**
   * Filter applied to every keydown. Mirrors the filter used by the
   * Sequencer's Delete handler and the Space-bar play shortcut, so
   * typing in a form field never triggers the piano.
   */
  _shouldHandle(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    const t = e.target;
    if (!t) return true;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return false;
    if (t.isContentEditable) return false;
    return true;
  }

  _setActive(pitch, on) {
    if (on) this.activeKeys.add(pitch);
    else this.activeKeys.delete(pitch);
    // Update the DOM in-place to avoid a full re-render on every key event.
    const btn = this.root.querySelector(`.piano-key[data-pitch="${pitch}"]`);
    if (btn) btn.classList.toggle('active', on);
  }

  selectedTrack() {
    const id = this.sequencer.selectedTrackId;
    if (!id) return null;
    return this.tm.getTrack(id);
  }

  /**
   * (Re)subscribe to the currently-selected track's onChange so the
   * banner updates when that track's metadata changes (e.g. an
   * instrument swap via drag-drop). Called from the constructor and
   * every time sequencer.onSelectionChange fires.
   *
   * Crucially we do NOT subscribe to this.tm.onChange — that fires for
   * every note addition across every track, which would trigger a full
   * piano-widget re-render on every recorded keypress. On iOS Safari,
   * wiping the piano DOM during a touchstart or keydown handler can
   * break the corresponding touchend/keyup event (the active state
   * stays "stuck" red and only resolves when the user stops recording).
   * By scoping our subscription to the selected track only AND applying
   * changes in-place via _updateBanner() (rather than re-rendering the
   * whole widget), the piano keyboard DOM survives the touch/key
   * sequence intact even during recording.
   */
  _resubscribeSelectedTrack() {
    if (this._unsubSelTrack) {
      this._unsubSelTrack();
      this._unsubSelTrack = null;
    }
    const track = this.selectedTrack();
    if (!track) return;
    this._unsubSelTrack = track.onChange(() => this._updateBanner());
  }

  /**
   * Update just the banner (track name + swatch color) in place, without
   * touching the piano-key DOM. Cheap enough to call on every note
   * addition / mixer change without risking the iOS Safari
   * mid-touch-DOM-mutation bug.
   */
  _updateBanner() {
    const track = this.selectedTrack();
    const swatch = this.root.querySelector('.piano-swatch');
    const name = this.root.querySelector('.piano-target-name');
    if (swatch) {
      if (track) swatch.style.setProperty('--swatch-color', track.color);
      else swatch.style.removeProperty('--swatch-color');
    }
    if (name) name.textContent = track ? track.name : '';
  }
}
